import { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'reactstrap';
import PropTypes from 'prop-types';
import { ArrowLeft, Save, MessageSquare, Check, XCircle } from 'react-feather';
import { AuthContext } from '../../../context/authContext';
import ModalOptions from '../../../layouts/modals/ModalOptions';
import matriculacionApiService from '../../../services/http/matriculacionApiService';
import ModalClickable from '../../../layouts/modals/ModalClickable';
import { getItem, STORAGE_KEYS } from '../../../services/storage';
import useSoloLecturaMatriculas from '../../../hooks/useSoloLecturaMatriculas';
import logger from '../../../services/logger';
import config from '../../../config';
import colors from '../../../theme/colors';

const btnBase = {
  display: 'inline-flex', alignItems: 'center', gap: '5px',
  fontSize: '0.75rem', fontWeight: 600, borderRadius: '6px',
  cursor: 'pointer', transition: 'all 0.15s', padding: '6px 14px',
};
const btnPrimary = { ...btnBase, border: 'none', backgroundColor: colors.primary, color: '#fff' };
const btnOutline = { ...btnBase, border: `1px solid ${colors.primary}`, backgroundColor: '#fff', color: colors.primary };
const btnSecondary = { ...btnBase, border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#6b7280' };
const btnDanger = { ...btnBase, border: 'none', backgroundColor: '#dc2626', color: '#fff' };
const disabled = (isDisabled) => isDisabled ? { cursor: 'not-allowed', opacity: 0.5 } : {};

const Acciones = ({
  isNoPresencial,
  curso,
  comentarios,
  modulos,
  activity,
  hoursCounter,
  precioFinal,
  excepcionFCT,
  modulosDirty,
}) => {
  const navigate = useNavigate();
  const soloLectura = useSoloLecturaMatriculas();
  const enrollmentSession = getItem(STORAGE_KEYS.ENROLLMENT);
  const [balance, setBalance] = useState(-1);
  const { userOU } = useContext(AuthContext);
  const [dataApi, setDataApi] = useState([]);
  const [tooltipBalance, setTooltipBalance] = useState({ tooltipOpen: false });
  const [isOpen, setIsOpen] = useState(false);
  const [isToolTipHoursOpened, setIsToolTipHoursOpened] = useState(false);
  const [url, setUrl] = useState('');
  const [raw, setRaw] = useState({});
  const [errorEstados, setErrorEstados] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const dataBalance = await matriculacionApiService.consultarBalance({
          headquarter: enrollmentSession.headquarter,
          id: enrollmentSession.id,
        });
        setBalance(parseFloat(dataBalance.Balance));
      } catch (error) {
        setBalance(-1);
      }

      try {
        const dataMatricula = await matriculacionApiService.detalleMatricula({
          headquarter: enrollmentSession.headquarter,
          id: enrollmentSession.id,
        });
        setDataApi(dataMatricula);
      } catch (error) {
        setDataApi([]);
      }
    };

    fetchData();
  }, [enrollmentSession?.headquarter, enrollmentSession?.id]);

  const updateComment = async () => {
    try {
      await matriculacionApiService.agregarComentario({
        headquarter: enrollmentSession.headquarter,
        numMatricula: enrollmentSession.id,
        comment: comentarios,
      });

      window.location.reload();
    } catch (error) {
      // No hacer nada específico aquí.
    }
  };

  const updateModules = async () => {
    try {
      await matriculacionApiService.actualizarModulos({
        headquarter: enrollmentSession.headquarter,
        numMatricula: enrollmentSession.id,
        modulos,
        activity,
      });
    } catch (error) {
      // No hacer nada específico aquí.
      logger.error(error);
    }
  };

  const handleOnClick = () => {
    navigate(-1);
  };

  // Función para cambiar el estado (Sede - Secretaria)
  const handleState = (state) => {
    setIsOpen(true);
    const rawInfo = {
      state,
      headquarter: enrollmentSession.headquarter,
      numMatricula: enrollmentSession.id,
      nombre: dataApi[0].sNombre,
      studentEmail: enrollmentSession.email,
      idDelegacion: enrollmentSession.headquarter === 'CEDESCA' ? userOU.idCedesca : userOU.idCntec,
      comentarios,
      curso,
    };
    setRaw(rawInfo);
    setUrl(`${config.REACT_APP_NODE_API}cambiarEstado`);
  };

  const handleSede = () => {
    handleState('6');
  };
  const handleSecretaria = () => {
    handleState('5');
  };

  const toggleTooltipBalance = () => {
    setTooltipBalance((prev) => ({
      tooltipOpen: !prev.tooltipOpen,
    }));
  };
  const toggleHours = () => {
    setIsToolTipHoursOpened(!isToolTipHoursOpened);
  };

  // Función para confirmar la petición
  const handleConfirm = () => {
    const actividadesValidas = [];

    // Usamos Object.entries para obtener las claves y los valores de 'activity'
    Object.entries(activity).forEach(([, actividades]) => {
      // Recorremos cada actividad dentro del módulo de 'activity'
      actividades.forEach((act) => {
        // Creamos la cadena que representa la combinación de modulo y descripcion
        const descripcionCompleta = `${act.modulo} ${act.descripcion}`;

        // Verificamos si la descripcion completa está en los modulos y si idEstado es -1
        if (modulos.includes(descripcionCompleta) && act.idEstado === -1) {
          actividadesValidas.push(act);
        }
      });
    });
    setErrorEstados(actividadesValidas);
    // Si no se encontraron actividades válidas, actualizamos los módulos
    if (actividadesValidas.length === 0) {
      setIsOpen(true);
      const rawInfo = {
        headquarter: enrollmentSession.headquarter,
        numMatricula: enrollmentSession.id,
        nombre: dataApi[0].sNombre,
        emailAlumno: enrollmentSession.email,
        idDelegacion:
          enrollmentSession.headquarter === 'CEDESCA' ? userOU.idCedesca : userOU.idCntec,
        comentarios,
        curso,
        precioTotal: precioFinal,
        estado: dataApi[0].estado,
        excepcionFCT,
      };
      setRaw(rawInfo);
      setUrl(`${config.REACT_APP_NODE_API}confirmarPeticion`);
    }
  };

  return (
    <>
      {errorEstados.length > 0 && (
        <ModalClickable
          header={`Faltan estados por definir en las actividades: ${errorEstados
            .map((e) => {
              return e.modulo + e.descripcion;
            })
            .join(', ')}`}
        />
      )}

      {isOpen && (
        <ModalOptions
          header="¿Desea realizar estos cambios?"
          url={url}
          raw={raw}
          load={setIsOpen}
          text1="Cancelar"
          color1="danger"
          text2="Aceptar"
          color2="primary"
          comment={comentarios.length > 0 ? updateComment : ''}
          module={modulosDirty ? updateModules : undefined}
        />
      )}
      {dataApi.length > 0 && dataApi[0].estado === 'Activa' && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
          <button type="button" onClick={handleOnClick} style={btnSecondary}>
            <ArrowLeft size={13} /> Atrás
          </button>
          {userOU.centro === 'SUPERADMIN' || userOU.centro === 'CNTEC Girona' || userOU.centro === 'CEDESCA' || userOU.centro === 'CNTEC Sevilla' || userOU.centro === 'SECRETARÍA' ? (
            <button type="button" onClick={handleConfirm} disabled={(isNoPresencial && hoursCounter > 2000) || comentarios.length === 0 || soloLectura} style={{ ...btnPrimary, ...disabled((isNoPresencial && hoursCounter > 2000) || comentarios.length === 0 || soloLectura) }}>
              <Save size={13} /> Guardar cambios
            </button>
          ) : (
            <button type="button" onClick={updateComment} disabled={comentarios.length === 0 || soloLectura} style={{ ...btnOutline, ...disabled(comentarios.length === 0 || soloLectura) }}>
              <MessageSquare size={13} /> Enviar comentario
            </button>
          )}
        </div>
      )}
      {dataApi.length > 0 && dataApi[0].estado === 'Revisar delegación' && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
          <button type="button" onClick={handleOnClick} style={btnSecondary}>
            <ArrowLeft size={13} /> Atrás
          </button>
          {userOU.centro === 'SUPERADMIN' || userOU.centro === 'CNTEC Girona' || userOU.centro === 'CEDESCA' || userOU.centro === 'CNTEC Sevilla' ? (
            <button type="button" onClick={updateComment} disabled={comentarios.length === 0 || soloLectura} style={{ ...btnOutline, ...disabled(comentarios.length === 0 || soloLectura) }}>
              <MessageSquare size={13} /> Enviar comentario
            </button>
          ) : (
            <button type="button" onClick={handleSecretaria} disabled={(isNoPresencial && hoursCounter > 2000) || comentarios.length === 0 || soloLectura} style={{ ...btnPrimary, ...disabled((isNoPresencial && hoursCounter > 2000) || comentarios.length === 0 || soloLectura) }}>
              <Check size={13} /> Confirmar revisión
            </button>
          )}
        </div>
      )}
      {dataApi.length > 0 && dataApi[0].estado === 'Revisar secretaria' && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
          <button type="button" onClick={handleOnClick} style={btnSecondary}>
            <ArrowLeft size={13} /> Atrás
          </button>
          {userOU.centro === 'SUPERADMIN' || userOU.centro === 'CNTEC Girona' || userOU.centro === 'CEDESCA' || userOU.centro === 'CNTEC Sevilla' || userOU.centro === 'SECRETARÍA' ? (
            <div style={{ display: 'flex', gap: '8px' }}>
              <span id="toolTipHours">
                {comentarios.length === 0 && (
                  <Tooltip placement="top" isOpen={isToolTipHoursOpened} target="toolTipHours" toggle={toggleHours}>
                    Realice cambios o escriba un comentario antes de rechazar.
                  </Tooltip>
                )}
                <button type="button" onClick={handleSede} disabled={comentarios.length === 0 || soloLectura} style={{ ...btnDanger, ...disabled(comentarios.length === 0 || soloLectura) }}>
                  <XCircle size={13} /> Rechazar
                </button>
              </span>
              <div id="Test">
                <button type="button" onClick={handleConfirm} disabled={balance < 0 || (isNoPresencial && hoursCounter > 2000) || soloLectura} style={{ ...btnPrimary, ...disabled(balance < 0 || (isNoPresencial && hoursCounter > 2000) || soloLectura) }}>
                  <Check size={13} /> Confirmar
                </button>
              </div>
              {balance < 0 && (
                <Tooltip placement="top" isOpen={tooltipBalance.tooltipOpen} target="Test" toggle={toggleTooltipBalance}>
                  No es posible confirmar: balance negativo de {balance} EUR
                </Tooltip>
              )}
            </div>
          ) : (
            <button type="button" onClick={updateComment} disabled={comentarios.length === 0 || soloLectura} style={{ ...btnOutline, ...disabled(comentarios.length === 0 || soloLectura) }}>
              <MessageSquare size={13} /> Enviar comentario
            </button>
          )}
        </div>
      )}
      {dataApi.length > 0 && (dataApi[0].estado === 'Baja' || dataApi[0].estado === 'Pendiente baja') && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
          <button type="button" onClick={handleOnClick} style={btnSecondary}>
            <ArrowLeft size={13} /> Atrás
          </button>
          <button type="button" onClick={updateComment} disabled={comentarios.length === 0 || soloLectura} style={{ ...btnOutline, ...disabled(comentarios.length === 0 || soloLectura) }}>
            <MessageSquare size={13} /> Enviar comentario
          </button>
        </div>
      )}
    </>
  );
};
Acciones.propTypes = {
  isNoPresencial: PropTypes.bool,
  curso: PropTypes.string,
  comentarios: PropTypes.string,
  modulos: PropTypes.array,
  activity: PropTypes.object,
  hoursCounter: PropTypes.number,
  precioFinal: PropTypes.number,
  excepcionFCT: PropTypes.string,
  modulosDirty: PropTypes.bool,
};
export default Acciones;
